import React, {useCallback, useEffect, useMemo, useState} from "react";

import { ColumnsType } from "antd/es/table";
import { Button, Input, message, Space, Table } from "antd";
import {RigServiceData} from "../../../../types/RigServiceData";
import {
    addRigServiceRow, deleteRigServiceByRsId,
    fetchRigServiceByPid,
    updateRigServiceRow
} from "../../../../backendInteraction/RigServiceApiFetch";

interface RigServiceTableProps {
    data: RigServiceData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
}

const RigServiceTable: React.FC<RigServiceTableProps> = ( {data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow })=> {
    const [rigServiceData, setRigServiceData] = useState<RigServiceData[]>([]);
    const [editingRsId, setEditingRsId] = useState<number | null>(null);
    const [editData, setEditData] = useState<RigServiceData | null>(null);

    useEffect(() => {
        setRigServiceData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingRsId !== -1 ) {
            const newRow: RigServiceData = {
                key: -1,
                rs_id: -1,
                service_type: "",
                daily: "",
                weekly: "",
                thousand_hour: "",
                pid: plodId ?? -1,
            }
            setRigServiceData(prevData => {
                const hasNewRow = prevData.some(item => item.rs_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            })

            setEditingRsId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingRsId]);


    useEffect(() => {
        if (isView) {
            if (editingRsId === -1) {
                setRigServiceData(prevData => prevData.filter(item => item.rs_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingRsId(null);
            setEditData(null);
        }
    }, [isView])


    const saveChanges = useCallback( async () => {
        console.log("Saving changes:", editData);

        if(!editData) return;

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingRsId === -1) {
            const success = await addRigServiceRow(editData, aid);
            if (success) {
                setRigServiceData( await fetchRigServiceByPid(plodId ?? -1, aid));
                message.success("New rig service added successfully!");
            } else {
                message.error("Failed to add new rig service.");
            }
        } else {
            const success = await updateRigServiceRow(editingRsId ?? -1, editData);
            if (success) {
                setRigServiceData( await fetchRigServiceByPid(plodId ?? -1, aid));
                message.success("Rig service updated successfully!");
            } else {
                message.error("Failed to update rig service.");
            }
        }

        if (editingRsId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingRsId(null);
        setEditData(null);
    }, [editData, editingRsId,setIsAddNewRow]);

    const cancelEditing = useCallback((record: RigServiceData) => {
        console.log("Cancel editing");
        setEditingRsId(null);
        setEditData(null);

        if (record.rs_id === -1) {
            setRigServiceData(prevData => prevData.filter(item => item.rs_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: RigServiceData) => {
        console.log("Editing row :", record);
        if ( editingRsId === -1 ) {
            setRigServiceData(prevData => prevData.filter(item => item.rs_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingRsId( prevId => {
            if (prevId !== null) cancelEditing({rs_id: prevId} as RigServiceData);
            return record.rs_id;
        });

        setEditData({ ...record }) ;
    }, [editingRsId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: RigServiceData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deleteRigServiceByRsId(record.rs_id);
        if (success) {
            setRigServiceData(await fetchRigServiceByPid(plodId ?? -1, aid));
            message.success("Rig service deleted successfully!");
        } else {
            message.error("Failed to delete rig service.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof RigServiceData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);


    const columns: ColumnsType<RigServiceData> = useMemo(() => [
        {
            title: "Service Type",
            dataIndex: "service_type",
            key: "service_type",
            width: "16%",
            render: (_, record) =>
                editingRsId === record.rs_id ? (
                    <Input
                        value={editData?.service_type}
                        onChange={(e) => handleInputChange("service_type", e.target.value)}
                    />
                ) : (
                    record.service_type
                ),
        },
        {
            title: 'Daily',
            dataIndex: 'daily',
            key: 'daily',
            width: "16%",
            render: (_, record) =>
                editingRsId === record.rs_id ? (
                    <Input
                        value={editData?.daily}
                        onChange={(e) => handleInputChange("daily", e.target.value)}
                    />
                ) : (
                    record.daily
                ),
        },
        {
            title: 'Weekly',
            dataIndex: 'weekly',
            key: 'weekly',
            width: "16%",
            render: (_, record) =>
                editingRsId === record.rs_id ? (
                    <Input
                        value={editData?.weekly}
                        onChange={(e) => handleInputChange("weekly", e.target.value)}
                    />
                ) : (
                    record.weekly
                ),
        },
        {
            title: '1000 Hour',
            dataIndex: 'thousand_hour',
            key: 'thousand_hour',
            width: "16%",
            render: (_, record) =>
                editingRsId === record.rs_id ? (
                    <Input
                        value={editData?.thousand_hour}
                        onChange={(e) => handleInputChange("thousand_hour", e.target.value)}
                    />
                ) : (
                    record.thousand_hour
                ),
        },
        ...(!isView
                ? [{
                    title: 'Action',
                    key: 'action',
                    width: "20%",
                    render: (_: any , record: RigServiceData) => (
                        <Space size="small">
                            <Space size="small">
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingRsId === record.rs_id ? saveChanges() : startEditing(record))}>
                                    {editingRsId === record.rs_id ? "Save" : "Edit"}
                                </Button>
                                <Button color="geekblue" variant="text"
                                        onClick={() => (editingRsId === record.rs_id ? cancelEditing(record) : deleteRow(record))}>
                                    {editingRsId === record.rs_id ? "Cancel" : "Delete"}
                                </Button>
                            </Space>
                        </Space>
                    ),
                }] as ColumnsType<RigServiceData>
                : []
        ),
    ], [isView, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<RigServiceData>
            columns={columns}
            pagination={false}
            dataSource={rigServiceData}
        />
    );
};

export default RigServiceTable;